"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AlgoliaQueryBuilder = exports.AlgoliaQueryFacetFilterItem = void 0;
const Base_1 = require("./Base");
const Common_1 = require("./Common");
class AlgoliaQueryFacetFilterItem extends Base_1.Base {
    id = '';
    name = '';
    key = '';
    key_unique = false;
    value = null;
    more = null;
    color = '';
    contrast = '';
    icon = '';
    skip;
    type = '';
    constructor(data) {
        super('', null);
        (0, Common_1.setObjectProperties)(this, data);
    }
    toJSON() {
        return Object.assign({}, this);
    }
    toMinJSON() {
        return this.toJSON();
    }
    toFullJSON() {
        return this.toJSON();
    }
}
exports.AlgoliaQueryFacetFilterItem = AlgoliaQueryFacetFilterItem;
class AlgoliaQueryBuilder extends Base_1.Base {
    name = '';
    query = '';
    typoTolerance = '';
    facetFilters = [];
    hitsPerPage = 100;
    restrictSearchableAttributes = [];
    numericFilters = [];
    page = 0;
    index = '';
    type = ''; // class being searched (e.g. contacts, threads, orders, etc)
    subtype = ''; // Allows saved searches to be differentiated within a single type (sales tab & quotes tab both search the same data set but on different pages)
    filterItems = [];
    and = false;
    aroundLatLng = '';
    insideBoundingBox = null;
    aroundRadius = null;
    constructor(data, type) {
        super(type || 'searches', 12);
        (0, Common_1.setObjectProperties)(this, data);
        this.filterItems = this.filterItems?.map(i => new AlgoliaQueryFacetFilterItem(i));
    }
    addFilter(item) {
        if (item.key_unique) {
            if (item.key) {
                // Remove any other items with the same key, because key_unique means there can
                // be only one filter with that key (e.g. last_date);
                for (let i of this.filterItems) {
                    if (i.key === item.key) {
                        this.remove('filterItems', i);
                    }
                }
            }
            else {
                console.warn(`Item is marked as key_unique but has no 'key' property set`, item);
            }
        }
        // Now add the filter.
        this.add('filterItems', item);
        this.build();
    }
    removeFilter(item) {
        this.remove('filterItems', item);
        this.build();
    }
    get namedFilterItems() {
        return this.filterItems?.reduce((items, i) => {
            if (i.name) {
                items.push(i);
            }
            return items;
        }, []) || [];
    }
    toSearchRequest() {
        this.build();
        let filters = '';
        // Build the AND filters, if any.
        let ands = this.filterItems.reduce((a, b) => {
            if (b.type === 'and' || (b.type === 'or' && this.and)) {
                a.push(b.value);
            }
            return a;
        }, []);
        let ors = this.filterItems.reduce((a, b) => {
            if (b.type === 'or' && !this.and) {
                a.push(b.value);
            }
            return a;
        }, []);
        if (ands.length && ors.length) {
            filters = `((${ands.join(' AND ')}) AND (${ors.join(' OR ')}))`;
        }
        else if (ands.length) {
            filters = `(${ands.join(' AND ')})`;
        }
        else if (ors.length) {
            filters = `(${ors.join(' OR ')})`;
        }
        let r = {
            query: this.query,
            params: {
                facetFilters: this.facetFilters,
                numericFilters: this.numericFilters,
                filters: filters,
                page: this.page,
                hitsPerPage: this.hitsPerPage,
                restrictSearchableAttributes: this.restrictSearchableAttributes
            }
        };
        if (this.typoTolerance) {
            r.typoTolerance = this.typoTolerance;
        }
        if (this.aroundLatLng) {
            r.params.aroundLatLng = this.aroundLatLng;
        }
        else if (this.insideBoundingBox) {
            r.params.insideBoundingBox = [this.insideBoundingBox];
        }
        if (this.aroundRadius) {
            r.params.aroundRadius = this.aroundRadius;
        }
        // console.log(r.params);
        return r;
    }
    build() {
        this.facetFilters = [];
        this.numericFilters = [];
        if (this.type) {
            this.facetFilters.push([`_type:${this.type}`]);
        }
        let groups = {};
        for (let i of this.filterItems) {
            if (i.skip)
                continue;
            if (i.key && !i.key_unique) {
                if (!groups[i.key]) {
                    groups[i.key] = { type: i.type, value: [] };
                }
                groups[i.key].value.push(i.value);
                if (i.more) {
                    groups[i.key].value.push(i.more);
                }
            }
            else {
                switch (i.type) {
                    case 'numeric':
                        this.numericFilters.push(i.value);
                        if (i.more) {
                            this.numericFilters.push(i.more);
                        }
                        break;
                    case 'facet':
                        this.facetFilters.push(i.value);
                        if (i.more) {
                            this.facetFilters.push(i.more);
                        }
                        break;
                }
            }
        }
        for (let key of Object.keys(groups)) {
            switch (groups[key].type) {
                case 'numeric':
                    this.numericFilters.push(groups[key].value);
                    break;
                case 'facet':
                    this.facetFilters.push(groups[key].value);
                    break;
            }
        }
    }
    async search(indexName, environment, algoliasearch) {
        if (environment?.algolia?.appId && environment?.algolia?.searchKey) {
            const aClient = algoliasearch(environment?.algolia?.appId, environment?.algolia?.searchKey);
            const index = aClient.initIndex(indexName);
            let req = Object.assign({}, this.toSearchRequest());
            req.params.facetFilters.push([`_env:${environment.type}`]);
            let results = await index.search(req.query, req.params);
            if (results?.hits) {
                results.hits
                    .sort((a, b) => {
                    let aC = a['_highlightResult']?.city?.name?.matchLevel === 'full';
                    let bC = b['_highlightResult']?.city?.name?.matchLevel === 'full';
                    let aA = a['_highlightResult']?.name?.matchLevel === 'full';
                    let bA = b['_highlightResult']?.name?.matchLevel === 'full';
                    let aI = a.icao?.match(/^K/);
                    let bI = b.icao?.match(/^K/);
                    return (aC && aA && aI && bC && bA && bI) ? 0 : (aC && aA && aI) ? -1 : (aC && aI) ? -1 : 1;
                });
            }
            return results;
        }
        else {
            console.warn('SEARCH SETTINGS NOT CONFIGURED');
        }
    }
    toJSON() {
        return { id: this.id || '', name: this.name || '', index: this.index, subtype: this.subtype, query: this.query || '', filterItems: this.namedFilterItems?.map(i => i.toJSON ? i.toJSON() : i) || [] };
    }
    toMinJSON(ignoreDocRef) {
        return this.toJSON();
    }
    toFullJSON() {
        return { id: this.id || '', name: this.name || '', index: this.index, subtype: this.subtype, query: this.query || '', filterItems: this.filterItems?.map(i => i.toJSON ? i.toJSON() : i) || [] };
    }
}
exports.AlgoliaQueryBuilder = AlgoliaQueryBuilder;
